import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  monitorPaymentTakenNoBooking,
  monitorBookingCreatedNoPayment,
  monitorApiEndpointFailure,
  monitorVenposApiError,
  monitorBasketCreationFailure,
  monitorPaymentInitializationFailure,
  checkMaintenanceModeStatus,
  resetMaintenanceMode
} from '../../../services/errorMonitoringService';
import { 
  getSystemStatus, 
  markBookingSystemDeprecated 
} from '../../../services/systemStatusService';
import { sendErrorNotification } from '../../../services/errorNotificationService';
import config from '../../../config/environment';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: Arial, sans-serif;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #101d40 0%, #2c3e50 100%);
  color: white;
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
`;

const Subtitle = styled.p`
  margin: 0;
  opacity: 0.9;
  font-size: 1rem;
`;

const StatusCard = styled.div`
  background: ${props => props.enabled ? '#fff3cd' : '#d4edda'};
  border: 2px solid ${props => props.enabled ? '#ffc107' : '#28a745'};
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const StatusTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  color: ${props => props.enabled ? '#856404' : '#155724'};
  font-size: 1.5rem;
`;

const StatusText = styled.p`
  margin: 0.5rem 0;
  color: ${props => props.enabled ? '#856404' : '#155724'};
`;

const Section = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #101d40;
  font-size: 1.25rem;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
`;

const ButtonGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const TestButton = styled.button`
  padding: 1rem 1.5rem;
  background: ${props => {
    if (props.variant === 'critical') return '#dc3545';
    if (props.variant === 'warning') return '#ffc107';
    if (props.variant === 'success') return '#28a745';
    return '#007bff';
  }};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    opacity: 0.9;
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const MaintenanceButton = styled(TestButton)`
  background: ${props => props.enabled ? '#28a745' : '#dc3545'};
  grid-column: 1 / -1;
  font-size: 1.1rem;
  padding: 1.25rem 2rem;
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  background: ${props => {
    if (props.type === 'success') return '#d4edda';
    if (props.type === 'error') return '#f8d7da';
    return '#d1ecf1';
  }};
  border: 1px solid ${props => {
    if (props.type === 'success') return '#c3e6cb';
    if (props.type === 'error') return '#f5c6cb';
    return '#bee5eb';
  }};
  color: ${props => {
    if (props.type === 'success') return '#155724';
    if (props.type === 'error') return '#721c24';
    return '#0c5460';
  }};
`;

const LogArea = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
  max-height: 300px;
  overflow-y: auto;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
`;

const LogEntry = styled.div`
  padding: 0.5rem 0;
  border-bottom: 1px solid #dee2e6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ErrorMonitoringTest = () => {
  const [maintenanceMode, setMaintenanceMode] = useState({ enabled: false, reason: '', timestamp: '' });
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [logs, setLogs] = useState([]);

  // Check maintenance mode status on mount and periodically
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const status = await checkMaintenanceModeStatus();
        setMaintenanceMode(status);
        
        // Also check system status
        const sysStatus = await getSystemStatus();
        setSystemStatus(sysStatus);
      } catch (error) {
        addLog('error', `Failed to check status: ${error.message}`);
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const addLog = (type, message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { type, message, timestamp }]);
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  // Generate realistic-looking ID
  const generateRealisticId = (prefix) => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `${prefix}${timestamp}${random}`;
  };

  // Test: Payment Taken But Booking Failed
  const testPaymentTakenNoBooking = async () => {
    setLoading(true);
    addLog('info', 'Testing: Payment Taken But Booking Failed...');
    
    try {
      const mockPaymentResult = {
        paymentToken: generateRealisticId('PS'),
        transactionId: generateRealisticId('TXN'),
        authCode: generateRealisticId('AUTH'),
        amount: 50.00,
        orderId: generateRealisticId('ORD')
      };
      
      const mockError = new Error('Booking creation failed after payment was processed successfully');
      
      await monitorPaymentTakenNoBooking(
        mockPaymentResult,
        mockPaymentResult.orderId,
        mockError
      );
      
      addLog('success', '✅ Test triggered! Check email and maintenance mode status.');
      showAlert('success', 'Payment Taken No Booking test triggered! Check your email.');
      
      // Refresh maintenance mode status
      setTimeout(async () => {
        const status = await checkMaintenanceModeStatus();
        setMaintenanceMode(status);
      }, 2000);
    } catch (error) {
      addLog('error', `Test failed: ${error.message}`);
      showAlert('error', `Test failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Test: Booking Created Without Payment
  const testBookingCreatedNoPayment = async () => {
    setLoading(true);
    addLog('info', 'Testing: Booking Created Without Payment...');
    
    try {
      const mockBookingData = {
        bookingReference: generateRealisticId('BK'),
        orderId: generateRealisticId('ORD'),
        totalAmount: 0,
        customerDetails: {
          firstName: 'John',
          lastName: 'Smith',
          email: 'customer@example.com'
        }
      };
      
      await monitorBookingCreatedNoPayment(mockBookingData, 50.00);
      
      addLog('success', '✅ Test triggered! Check email and maintenance mode status.');
      showAlert('success', 'Booking Created No Payment test triggered! Check your email.');
      
      setTimeout(async () => {
        const status = await checkMaintenanceModeStatus();
        setMaintenanceMode(status);
      }, 2000);
    } catch (error) {
      addLog('error', `Test failed: ${error.message}`);
      showAlert('error', `Test failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Test: API Endpoint Failure
  const testApiEndpointFailure = async () => {
    setLoading(true);
    addLog('info', 'Testing: API Endpoint Failure...');
    
    try {
      const mockError = new Error('API endpoint returned 500 Internal Server Error');
      
      await monitorApiEndpointFailure(
        'POST /Payment',
        mockError,
        { orderId: generateRealisticId('ORD'), amount: 50.00 }
      );
      
      addLog('success', '✅ Test triggered! (May need 3 failures to trigger maintenance mode)');
      showAlert('info', 'API Endpoint Failure test triggered!');
    } catch (error) {
      addLog('error', `Test failed: ${error.message}`);
      showAlert('error', `Test failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Test: Venpos API Error
  const testVenposApiError = async () => {
    setLoading(true);
    addLog('info', 'Testing: Venpos API Error...');
    
    try {
      const mockError = new Error('Venpos API returned timeout error');
      
      await monitorVenposApiError(
        'Basket/Load',
        mockError,
        { basketId: generateRealisticId('BSK') }
      );
      
      addLog('success', '✅ Test triggered! (May need 3 failures to trigger maintenance mode)');
      showAlert('info', 'Venpos API Error test triggered!');
    } catch (error) {
      addLog('error', `Test failed: ${error.message}`);
      showAlert('error', `Test failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Test: Basket Creation Failure
  const testBasketCreationFailure = async () => {
    setLoading(true);
    addLog('info', 'Testing: Basket Creation Failure...');
    
    try {
      const mockError = new Error('Basket creation failed: Invalid location ID');
      
      await monitorBasketCreationFailure(mockError, { locationId: 978 });
      
      addLog('success', '✅ Test triggered! (May need 3 failures to trigger maintenance mode)');
      showAlert('info', 'Basket Creation Failure test triggered!');
    } catch (error) {
      addLog('error', `Test failed: ${error.message}`);
      showAlert('error', `Test failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Test: Payment Initialization Failure
  const testPaymentInitializationFailure = async () => {
    setLoading(true);
    addLog('info', 'Testing: Payment Initialization Failure...');
    
    try {
      const mockError = new Error('Payment initialization failed: Payment gateway connection timeout');
      
      await monitorPaymentInitializationFailure(mockError, { basketId: generateRealisticId('BSK') });
      
      addLog('success', '✅ Test triggered! (May need 3 failures to trigger maintenance mode)');
      showAlert('info', 'Payment Initialization Failure test triggered!');
    } catch (error) {
      addLog('error', `Test failed: ${error.message}`);
      showAlert('error', `Test failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Test: Booking Metadata Missing (System Status Deprecated)
  const testBookingMetadataMissing = async () => {
    setLoading(true);
    addLog('info', 'Testing: Booking Metadata Missing (System Status Deprecated)...');
    
    try {
      await markBookingSystemDeprecated({
        pageName: 'Flower & Garden Show',
        errorMessage: 'Missing event metadata after all fallback attempts',
        basketId: generateRealisticId('BSK'),
        customerEmail: 'customer@example.com',
        updates: [
          {
            description: 'Automatic fallback triggered - event metadata unavailable',
            timestamp: new Date().toLocaleString('en-GB', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })
          }
        ]
      });
      
      addLog('success', '✅ Test triggered! System Status updated to DEPRECATED.');
      showAlert('success', 'Booking System marked as DEPRECATED! Check System Status page.');
      
      // Refresh system status
      setTimeout(async () => {
        const sysStatus = await getSystemStatus();
        setSystemStatus(sysStatus);
      }, 2000);
    } catch (error) {
      addLog('error', `Test failed: ${error.message}`);
      showAlert('error', `Test failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Test: Rate Limit Hit / Potential Attack Detected
  const testRateLimitAttack = async () => {
    setLoading(true);
    addLog('info', 'Testing: Rate Limit Hit / Potential Attack Detected...');
    
    try {
      const ipAddress = `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`;
      const requestCount = Math.floor(Math.random() * 50) + 100; // 100-150 requests
      const timeWindow = '5 minutes';
      
      await sendErrorNotification({
        errorType: 'RATE_LIMIT_EXCEEDED',
        errorMessage: `Rate limit exceeded: ${requestCount} requests from IP ${ipAddress} within ${timeWindow}. Potential automated attack or brute force attempt detected.`,
        endpoint: 'POST /Payment',
        context: {
          ipAddress: ipAddress,
          requestCount: requestCount,
          timeWindow: timeWindow,
          userAgent: 'Mozilla/5.0 (compatible; suspicious-bot/1.0)',
          endpoint: 'POST /Payment',
          attackType: 'Rate Limit Exceeded',
          severity: 'HIGH',
          blocked: true,
          timestamp: new Date().toISOString()
        },
        orderId: generateRealisticId('ORD'),
        timestamp: new Date().toISOString()
      });
      
      addLog('success', '✅ Attack detection test triggered! Check email.');
      showAlert('success', 'Rate Limit / Attack Detection test triggered! Check your email.');
    } catch (error) {
      addLog('error', `Test failed: ${error.message}`);
      showAlert('error', `Test failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Toggle Maintenance Mode
  const toggleMaintenanceMode = async () => {
    setLoading(true);
    
    try {
      if (maintenanceMode.enabled) {
        addLog('info', 'Disabling maintenance mode...');
        await resetMaintenanceMode();
        addLog('success', '✅ Maintenance mode disabled!');
        showAlert('success', 'Maintenance mode disabled successfully!');
      } else {
        addLog('info', 'Enabling maintenance mode...');
        const apiUrl = `${config.apiEndpoint}/custom/v1/enable-maintenance-mode`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reason: 'Manually enabled via admin test page'
          })
        });
        
        if (response.ok) {
          addLog('success', '✅ Maintenance mode enabled!');
          showAlert('success', 'Maintenance mode enabled successfully!');
        } else {
          throw new Error('Failed to enable maintenance mode');
        }
      }
      
      // Refresh status
      const status = await checkMaintenanceModeStatus();
      setMaintenanceMode(status);
    } catch (error) {
      addLog('error', `Failed: ${error.message}`);
      showAlert('error', `Failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Check Complete - Verify error detection system is active and working
  const handleCheckComplete = async () => {
    setLoading(true);
    addLog('info', 'Running complete system check...');
    
    try {
      // Check maintenance mode status
      const maintenanceStatus = await checkMaintenanceModeStatus();
      addLog('info', `Maintenance mode: ${maintenanceStatus.enabled ? 'ACTIVE' : 'INACTIVE'}`);
      
      // Check system status
      const sysStatus = await getSystemStatus();
      const bookingSystemStatus = sysStatus?.systems?.find(s => s.id === 'booking')?.status || 'unknown';
      addLog('info', `Booking system status: ${bookingSystemStatus.toUpperCase()}`);
      
      // Test error notification endpoint
      const testNotification = await sendErrorNotification({
        errorType: 'SYSTEM_CHECK',
        errorMessage: 'Automated system check - error detection is active and working',
        endpoint: 'System Health Check',
        context: {
          checkType: 'Complete System Verification',
          maintenanceMode: maintenanceStatus.enabled ? 'ACTIVE' : 'INACTIVE',
          bookingSystemStatus: bookingSystemStatus,
          timestamp: new Date().toISOString()
        },
        timestamp: new Date().toISOString()
      });
      
      if (testNotification) {
        addLog('success', '✅ Error notification system: WORKING');
      } else {
        throw new Error('Error notification test failed');
      }
      
      // Send completion email
      const completionEmailBody = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #333; background: #f5f5f5; margin: 0; padding: 0;">
  <div style="max-width: 700px; margin: 0 auto; padding: 20px; background: #ffffff;">
    
    <!-- Header -->
    <div style="background-color: #28a745; background: linear-gradient(135deg, #28a745 0%, #20c997 100%); color: #ffffff !important; padding: 30px 25px; border-radius: 12px; margin: -20px -20px 25px -20px; border: 3px solid #155724;">
      <div style="text-align: center;">
        <div style="font-size: 48px; margin-bottom: 15px; color: #ffffff !important;">✅</div>
        <h1 style="margin: 0; font-size: 28px; font-weight: 700; color: #ffffff !important;">SYSTEM CHECK COMPLETE</h1>
        <div style="margin-top: 10px; font-size: 18px; color: #ffffff !important; opacity: 0.95; font-weight: 600;">
          Error Detection System: ACTIVE & OPERATIONAL
        </div>
      </div>
    </div>
    
    <!-- Success Banner -->
    <div style="background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%); border: 3px solid #28a745; color: #155724 !important; padding: 30px; border-radius: 10px; margin-bottom: 25px; box-shadow: 0 4px 6px rgba(40, 167, 69, 0.2);">
      <div style="font-size: 22px; font-weight: 900; margin-bottom: 15px; text-align: center; color: #155724 !important; text-transform: uppercase;">
        ✅ SUCCESSFUL - ALL SYSTEMS OPERATIONAL
      </div>
      <div style="font-size: 16px; line-height: 1.8; text-align: center; color: #155724 !important;">
        <strong style="color: #155724 !important; font-size: 18px;">🛡️ Error Detection System Status:</strong><br><br>
        <span style="color: #155724 !important;"><strong style="color: #155724 !important;">✅ ACTIVE AND WORKING</strong></span><br><br>
        The automated error detection and monitoring system is <strong style="color: #155724 !important;">actively running</strong> and continuously monitoring the Belvoir Castle Booking System for:<br><br>
        <span style="color: #155724 !important; font-weight: 700;">• Errors and system failures</span><br>
        <span style="color: #155724 !important; font-weight: 700;">• Data corruption risks</span><br>
        <span style="color: #155724 !important; font-weight: 700;">• Payment and booking inconsistencies</span><br>
        <span style="color: #155724 !important; font-weight: 700;">• Security threats and attackers</span><br>
        <span style="color: #155724 !important; font-weight: 700;">• API failures and system errors</span><br><br>
        <span style="color: #155724 !important;"><strong style="color: #155724 !important;">The system runs automatically 24/7 to prevent loss, corruption of data, and prevent errors in the Belvoir Castle Booking System.</strong></span>
      </div>
    </div>
    
    <!-- System Status Details -->
    <div style="background: #ffffff; border: 2px solid #e9ecef; border-radius: 10px; padding: 20px; margin-bottom: 20px;">
      <h2 style="margin: 0 0 15px 0; font-size: 20px; color: #101d40; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; display: flex; align-items: center; gap: 10px;">
        <span style="font-size: 24px;">📊</span> System Status Details
      </h2>
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px;">
        <div style="padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #28a745;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Maintenance Mode</div>
          <div style="font-size: 18px; font-weight: 700; color: #28a745;">${maintenanceStatus.enabled ? 'ACTIVE' : 'INACTIVE'}</div>
        </div>
        <div style="padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #28a745;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Booking System</div>
          <div style="font-size: 18px; font-weight: 700; color: #28a745;">${bookingSystemStatus.toUpperCase()}</div>
        </div>
        <div style="padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #28a745;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Error Detection</div>
          <div style="font-size: 18px; font-weight: 700; color: #28a745;">ACTIVE</div>
        </div>
        <div style="padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #28a745;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Check Time</div>
          <div style="font-size: 14px; font-weight: 600; color: #333;">${new Date().toLocaleString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
        </div>
      </div>
    </div>
    
    <!-- Protection Details -->
    <div style="background: linear-gradient(135deg, #e7f3ff 0%, #d1ecf1 100%); border: 2px solid #17a2b8; border-radius: 10px; padding: 25px; margin-bottom: 20px;">
      <h2 style="margin: 0 0 15px 0; font-size: 20px; color: #0c5460; display: flex; align-items: center; gap: 10px;">
        <span style="font-size: 24px;">🛡️</span> Active Protection Features
      </h2>
      <div style="color: #0c5460; font-size: 15px; line-height: 1.8;">
        <p style="margin: 0 0 12px 0;"><strong>✅ Real-time Error Monitoring:</strong> Continuously monitors all booking transactions for errors and inconsistencies</p>
        <p style="margin: 0 0 12px 0;"><strong>✅ Automatic Data Integrity Protection:</strong> Detects and prevents data corruption before it occurs</p>
        <p style="margin: 0 0 12px 0;"><strong>✅ Payment Verification:</strong> Ensures payments and bookings are always synchronized</p>
        <p style="margin: 0 0 12px 0;"><strong>✅ Security Threat Detection:</strong> Monitors for suspicious activity and potential attackers</p>
        <p style="margin: 0 0 12px 0;"><strong>✅ Automatic Maintenance Mode:</strong> Activates protection mode when critical errors are detected</p>
        <p style="margin: 0;"><strong>✅ Email Alert System:</strong> Immediate notifications sent to jhartwell@belvoircastle.com when issues are detected</p>
      </div>
    </div>
    
    <!-- Footer -->
    <div style="background: #f8f9fa; padding: 20px; border-radius: 10px; margin-top: 25px; text-align: center; border-top: 3px solid #28a745;">
      <div style="font-size: 12px; color: #6c757d; line-height: 1.6;">
        <p style="margin: 0 0 5px 0; font-size: 16px; font-weight: 700; color: #101d40;"><strong>🛡️ Belvoir Castle Booking System - Automated Protection System</strong></p>
        <p style="margin: 0 0 10px 0; font-size: 14px; color: #28a745; font-weight: 600;">
          <strong>✅ System Check Completed Successfully</strong>
        </p>
        <p style="margin: 0; font-size: 13px; color: #666;">
          This automated system check confirms that error detection is active and working. The system is continuously monitoring for errors, data corruption risks, and security threats to protect the Belvoir Castle Booking System.
        </p>
        <p style="margin: 15px 0 0 0; padding-top: 15px; border-top: 1px solid #dee2e6; font-size: 11px; color: #999;">
          Check completed: ${new Date().toLocaleString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </p>
      </div>
    </div>
    
  </div>
</body>
</html>`;

      const emailResponse = await fetch(`${config.apiEndpoint}/custom/v1/send-error-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: 'jhartwell@belvoircastle.com',
          subject: '✅ System Check Complete - Error Detection Active & Working',
          body: completionEmailBody,
          errorDetails: {
            errorType: 'SYSTEM_CHECK_COMPLETE',
            endpoint: 'System Health Check',
            timestamp: new Date().toISOString(),
            maintenanceMode: maintenanceStatus.enabled ? 'ACTIVE' : 'INACTIVE',
            bookingSystemStatus: bookingSystemStatus
          }
        })
      });
      
      if (emailResponse.ok) {
        addLog('success', '✅ Completion email sent successfully!');
        showAlert('success', '✅ System Check Complete! Error detection is active and working. Email sent to jhartwell@belvoircastle.com');
      } else {
        throw new Error('Failed to send completion email');
      }
      
    } catch (error) {
      addLog('error', `Check failed: ${error.message}`);
      showAlert('error', `Check failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>🔧 Error Monitoring Test Page</Title>
        <Subtitle>Test error triggers and maintenance mode functionality</Subtitle>
      </Header>

      {alert && (
        <Alert type={alert.type}>
          {alert.message}
        </Alert>
      )}

      <StatusCard enabled={maintenanceMode.enabled}>
        <StatusTitle>
          {maintenanceMode.enabled ? '🚨 Maintenance Mode: ACTIVE' : '✅ Maintenance Mode: INACTIVE'}
        </StatusTitle>
        {maintenanceMode.enabled && (
          <>
            <StatusText><strong>Reason:</strong> {maintenanceMode.reason || 'No reason provided'}</StatusText>
            {maintenanceMode.timestamp && (
              <StatusText><strong>Enabled:</strong> {new Date(maintenanceMode.timestamp).toLocaleString('en-GB')}</StatusText>
            )}
          </>
        )}
        {!maintenanceMode.enabled && (
          <StatusText>System is operating normally. No maintenance mode active.</StatusText>
        )}
      </StatusCard>

      {systemStatus && (
        <StatusCard enabled={systemStatus.systems?.find(s => s.id === 'booking')?.status === 'degraded'}>
          <StatusTitle>
            {systemStatus.systems?.find(s => s.id === 'booking')?.status === 'degraded' 
              ? '⚠️ Booking System: DEPRECATED' 
              : '✅ Booking System: OPERATIONAL'}
          </StatusTitle>
          {systemStatus.systems?.find(s => s.id === 'booking')?.status === 'degraded' && (
            <>
              <StatusText>
                <strong>Description:</strong> {systemStatus.systems?.find(s => s.id === 'booking')?.description}
              </StatusText>
              {systemStatus.systems?.find(s => s.id === 'booking')?.incident && (
                <StatusText>
                  <strong>Incident:</strong> {systemStatus.systems?.find(s => s.id === 'booking')?.incident?.description}
                </StatusText>
              )}
            </>
          )}
          {systemStatus.systems?.find(s => s.id === 'booking')?.status !== 'degraded' && (
            <StatusText>Booking system is fully operational.</StatusText>
          )}
          <StatusText style={{ fontSize: '0.9rem', marginTop: '1rem', opacity: 0.8 }}>
            <a href="/status" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
              View Full System Status Page →
            </a>
          </StatusText>
        </StatusCard>
      )}

      <Section>
        <SectionTitle>🔴 Critical Error Tests (Immediate Maintenance Mode)</SectionTitle>
        <p style={{ marginBottom: '1rem', color: '#666' }}>
          These tests will immediately trigger maintenance mode and send urgent email alerts.
        </p>
        <ButtonGroup>
          <TestButton 
            variant="critical" 
            onClick={testPaymentTakenNoBooking}
            disabled={loading}
          >
            🚨 Test: Payment Taken But Booking Failed
          </TestButton>
          <TestButton 
            variant="critical" 
            onClick={testBookingCreatedNoPayment}
            disabled={loading}
          >
            🚨 Test: Booking Created Without Payment
          </TestButton>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>⚠️ Threshold-Based Error Tests</SectionTitle>
        <p style={{ marginBottom: '1rem', color: '#666' }}>
          These tests trigger after 3 failures within 5 minutes. Trigger multiple times to test threshold.
        </p>
        <ButtonGroup>
          <TestButton 
            variant="warning" 
            onClick={testApiEndpointFailure}
            disabled={loading}
          >
            ⚠️ Test: API Endpoint Failure
          </TestButton>
          <TestButton 
            variant="warning" 
            onClick={testVenposApiError}
            disabled={loading}
          >
            ⚠️ Test: Venpos API Error
          </TestButton>
          <TestButton 
            variant="warning" 
            onClick={testBasketCreationFailure}
            disabled={loading}
          >
            ⚠️ Test: Basket Creation Failure
          </TestButton>
          <TestButton 
            variant="warning" 
            onClick={testPaymentInitializationFailure}
            disabled={loading}
          >
            ⚠️ Test: Payment Initialization Failure
          </TestButton>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>🔧 System Status Tests (New)</SectionTitle>
        <p style={{ marginBottom: '1rem', color: '#666' }}>
          Tests for the new System Status integration. Marks booking system as DEPRECATED and sends email.
        </p>
        <ButtonGroup>
          <TestButton 
            variant="warning" 
            onClick={testBookingMetadataMissing}
            disabled={loading}
          >
            🔧 Test: Booking Metadata Missing (System Status)
          </TestButton>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>🛡️ Security & Attack Detection Tests</SectionTitle>
        <p style={{ marginBottom: '1rem', color: '#666' }}>
          Tests for security monitoring and attack detection. Simulates rate limiting and potential attack scenarios.
        </p>
        <ButtonGroup>
          <TestButton 
            variant="critical" 
            onClick={testRateLimitAttack}
            disabled={loading}
          >
            🛡️ Test: Rate Limit Hit / Attack Blocked
          </TestButton>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>⚙️ Maintenance Mode Control</SectionTitle>
        <p style={{ marginBottom: '1rem', color: '#666' }}>
          Manually enable or disable maintenance mode. Status updates automatically every 5 seconds.
        </p>
        <ButtonGroup>
          <MaintenanceButton 
            enabled={!maintenanceMode.enabled}
            onClick={toggleMaintenanceMode}
            disabled={loading}
          >
            {maintenanceMode.enabled ? '✅ Disable Maintenance Mode' : '🚨 Enable Maintenance Mode'}
          </MaintenanceButton>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>✅ System Check Complete</SectionTitle>
        <p style={{ marginBottom: '1rem', color: '#666' }}>
          Verify that error detection is active and working. This will check all systems and send a confirmation email.
        </p>
        <ButtonGroup>
          <TestButton 
            variant="success"
            onClick={handleCheckComplete}
            disabled={loading}
            style={{ 
              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
              fontSize: '1.1rem',
              padding: '1.25rem 2rem',
              gridColumn: '1 / -1'
            }}
          >
            ✅ Check Complete - Verify Error Detection System
          </TestButton>
        </ButtonGroup>
      </Section>

      <Section>
        <SectionTitle>📋 Test Logs</SectionTitle>
        <LogArea>
          {logs.length === 0 ? (
            <div style={{ color: '#999', fontStyle: 'italic' }}>No logs yet. Trigger a test to see activity.</div>
          ) : (
            logs.map((log, index) => (
              <LogEntry key={index}>
                <span style={{ color: '#666' }}>[{log.timestamp}]</span>{' '}
                <span style={{ 
                  color: log.type === 'success' ? '#28a745' : log.type === 'error' ? '#dc3545' : '#007bff',
                  fontWeight: 'bold'
                }}>
                  {log.type.toUpperCase()}
                </span>{' '}
                {log.message}
              </LogEntry>
            ))
          )}
        </LogArea>
      </Section>

      <Section>
        <SectionTitle>ℹ️ Information</SectionTitle>
        <div style={{ color: '#666', lineHeight: '1.8' }}>
          <p><strong>Email Notifications:</strong> All tests send emails to jhartwell@belvoircastle.com</p>
          <p><strong>Critical Tests:</strong> Immediately trigger maintenance mode and send urgent emails</p>
          <p><strong>Threshold Tests:</strong> Require 3 failures within 5 minutes to trigger maintenance mode</p>
          <p><strong>System Status Tests:</strong> Update System Status page and send email notifications (NEW)</p>
          <p><strong>Maintenance Mode:</strong> When active, booking journey shows maintenance message to users</p>
          <p><strong>Status Updates:</strong> All statuses refresh every 5 seconds</p>
          <p><strong>System Status Page:</strong> View public-facing status at <a href="/status" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>/status</a></p>
        </div>
      </Section>
    </Container>
  );
};

export default ErrorMonitoringTest;

